import { useState, useEffect, useMemo } from "react";
import { useFilterContext } from "./ContextManager";
import style from "../css/modalAirportsRoutes.module.css";

import planeIcon from "../assets/svg/plane.svg";

import { IconButton } from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import { Chip } from "@mui/material";

import { roundDecimals } from "../utils/utilityFunctions";

import { PriceData } from "./PriceData";

// Helper functions
const formatTime = (minutes, applyColor = false, isSlower = false) => {
  if (minutes == null) return null;
  const absMinutes = Math.abs(minutes);
  const hours = Math.floor(absMinutes / 60);
  const mins = absMinutes % 60;
  let timeString = "";
  if (hours > 0) {
    timeString += `${hours}h`;
  }
  if (hours > 0 && mins > 0) {
    timeString += `, ${mins}m`;
  } else if (mins > 0 || hours === 0) {
    timeString += `${mins} min`;
  }
  if (applyColor) {
    const color = minutes < 0 ? "red" : "green";
    const sign = minutes < 0 ? "-" : "+";
    return (
      <span style={{ color }}>
        {sign}
        {timeString}
      </span>
    );
  } else if (isSlower) {
    return <span style={{ textDecoration: "line-through" }}>{timeString}</span>;
  } else {
    return timeString;
  }
};

/*const formatRouteSpeedComparison = (ratio) => {
  let value, color;
  const parsedRatio = parseFloat(ratio);
  if (ratio === "1.0") {
    value = "1.0x";
    color = "black";
  } else if (parsedRatio > 1) {
    value = `${parsedRatio.toFixed(1)}x`;
    color = "green";
  } else if (parsedRatio > 0) {
    const inverseRatio = (1 / parsedRatio).toFixed(1);
    value = `${inverseRatio}x`;
    color = "red";
  } else {
    // Handle zero or invalid ratio
    value = "N/A";
    color = "gray";
  }
  return <span style={{ color }}>{value}</span>;
}; */

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
const pickColor = (arr, idx) =>
  Array.isArray(arr) && arr.length
    ? arr[clamp(idx, 0, arr.length - 1)]
    : undefined;

// simple contrast helper so text stays readable
const getContrastColor = (hex) => {
  if (!hex) return "#fff";
  const h = hex.replace("#", "");
  const r = parseInt(h.length === 3 ? h[0] + h[0] : h.slice(0, 2), 16);
  const g = parseInt(h.length === 3 ? h[1] + h[1] : h.slice(2, 4), 16);
  const b = parseInt(h.length === 3 ? h[2] + h[2] : h.slice(4, 6), 16);
  // perceived luminance
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum > 0.55 ? "#000" : "#fff";
};

export const RouteModal = () => {
  const {
    filteredRoutesResult,
    filteredAirportsResult,
    selectedRoutes,
    routeModalOpenState,
    updateRouteModalState,
    filters,
    selectedProfile,
    toggleSelectRoute,
  } = useFilterContext();

  const [modalClass, setModalClass] = useState(
    `${style.modal} ${style.hidden}`
  );
  const [isVisible, setIsVisible] = useState(false);
  const [isTimeOpen, setIsTimeOpen] = useState(false);
  const [isPriceExpanded, setIsPriceExpanded] = useState(false);

  const singleSelectedRouteId = useMemo(() => {
    return Array.isArray(selectedRoutes) && selectedRoutes.length === 1
      ? selectedRoutes[0]
      : null;
  }, [selectedRoutes]);

  const [isEmissionsOpen, setEmissionsOpen] = useState(false);
  const toggleEmissionsOpen = () => setEmissionsOpen((o) => !o);

  useEffect(() => {
    if (routeModalOpenState) {
      setIsVisible(true);
      setModalClass(`${style.modal} ${style.open}`);
    } else {
      setModalClass(`${style.modal} ${style.close}`);
      setTimeout(() => {
        setModalClass(`${style.modal} ${style.close} ${style.hidden}`);
      }, 300);
    }
  }, [routeModalOpenState]);

  useEffect(() => {
    if (singleSelectedRouteId) {
      updateRouteModalState(true);
    } else {
      updateRouteModalState(false);
    }
  }, [singleSelectedRouteId]);

  // Close modal if none or more than one route is selected
  useEffect(() => {
    if (!singleSelectedRouteId) return;

    const currentRoute = filteredRoutesResult?.find(
      (r) => r._id === singleSelectedRouteId
    );
    if (!currentRoute && routeModalOpenState) {
      updateRouteModalState(false);
    }
  }, [
    filteredRoutesResult,
    singleSelectedRouteId,
    routeModalOpenState,
    updateRouteModalState,
  ]);

  // Compute route and airport details
  const route = useMemo(() => {
    if (!singleSelectedRouteId) return null;
    return filteredRoutesResult?.find((r) => r._id === singleSelectedRouteId);
  }, [filteredRoutesResult, singleSelectedRouteId]);

  const airport1Details = useMemo(() => {
    return route
      ? filteredAirportsResult?.find(
          (airport) => airport.airportId === route.airport_1
        )
      : null;
  }, [route, filteredAirportsResult]);

  const airport2Details = useMemo(() => {
    return route
      ? filteredAirportsResult?.find(
          (airport) => airport.airportId === route.airport_2
        )
      : null;
  }, [route, filteredAirportsResult]);

  // Calculate and format flight times
  const { travelTime, flightTime, ratioTime, timeDifference } = useMemo(() => {
    if (!route || !filters) return {};

    let alternativeTravelTimes = [];
    if (route.travel) {
      alternativeTravelTimes = ["car", "transit"]
        .map((type) => route.travel?.[type])
        .filter((n) => Number.isFinite(n));
    }

    const shortestTime =
      alternativeTravelTimes.length > 0
        ? Math.min(...alternativeTravelTimes)
        : undefined;

    // Calculate city center times based on municipality
    let cityCenterTime;
    if (
      airport1Details?.municipality &&
      airport2Details?.municipality &&
      airport1Details.municipality === airport2Details.municipality
    ) {
      // If same municipality, use the shortest time
      cityCenterTime = Math.min(
        airport1Details.timeToCityCenter || 0,
        airport2Details.timeToCityCenter || 0
      );
    } else {
      // If different municipalities, use sum of both times
      cityCenterTime =
        (airport1Details?.timeToCityCenter || 0) +
        (airport2Details?.timeToCityCenter || 0);
    }

    const baseTime =
      cityCenterTime + (filters.departure || 0) + (filters.arrival || 0);

    const travelTime =
      filters.speed && route.distance && filters.speed > 0 && route.distance > 0
        ? Math.ceil((route.distance / filters.speed) * 60)
        : 0;

    const flightTime = baseTime + travelTime;

    /*const ratioTime = shortestTime
      ? (shortestTime / flightTime).toFixed(1)
      : "0.0";*/
    const timeDifference = shortestTime
      ? Math.ceil(shortestTime - flightTime)
      : null;

    return {
      shortestTime,
      baseTime,
      travelTime,
      flightTime,
      /*ratioTime,*/
      timeDifference,
    };
  }, [route, filters, selectedProfile]);

  const chipStyles = useMemo(() => {
    const idx = filters?.colorIndex ?? 0;
    const scheduledBg =
      pickColor(filters?.colors?.commercialRoute, idx) || "#1976d2";
    const psoBg = pickColor(filters?.colors?.psoRoute, idx) || "#6d4caf";

    return {
      scheduled: {
        backgroundColor: scheduledBg,
        color: getContrastColor(scheduledBg),
      },
      pso: { backgroundColor: psoBg, color: getContrastColor(psoBg) },
    };
  }, [filters?.colorIndex, filters?.colors]);

  const formattedFlightTime =
    route && flightTime != null ? formatTime(flightTime) : "N/A";

  const carTimeExists = route && filters.compareRoutes && route.travel?.car > 0;
  const transitTimeExists =
    route && filters.compareRoutes && route.travel?.transit > 0;
  const carIsSlower =
    carTimeExists &&
    transitTimeExists &&
    route.travel?.car > route.travel?.transit;
  const transitIsSlower =
    carTimeExists &&
    transitTimeExists &&
    route.travel?.transit > route.travel?.car;

  // Event handlers
  const toggleTimeOpen = () => {
    setIsTimeOpen((prev) => !prev);
  };

  const handleCloseModal = () => {
    if (singleSelectedRouteId) {
      // Unselect the route so the modal doesn't pop back open
      toggleSelectRoute(singleSelectedRouteId);
    }
    updateRouteModalState(false);
  }; // Early return if modal should not be visible
  if (!isVisible) {
    return null;
  }

  if (!route || !airport1Details || !airport2Details) {
    return null;
  }

  // Update modal class when price data expands
  const modalClasses = `${modalClass} ${
    isPriceExpanded ? style.modalExpanded : ""
  }`;

  const commercialColor = "pink";

  const getProfileName = () => {
    const names = {
      elflyNoemi: "Elfly Noemi",
      heartES30: "Heart ES-30",
    };
    return selectedProfile !== "default" ? names[selectedProfile] : "";
  };

  return (
    <div className={modalClasses}>
      <div className={style.closeButton}>
        {" "}
        <IconButton
          className={style.modalIconButton}
          aria-label="close modal"
          onClick={handleCloseModal}
        >
          <HighlightOffIcon className={style.modalIcon} />
        </IconButton>{" "}
      </div>
      <div className={style.wrapper}>
        <div className={style.modalContent}>
          {/* Airport details and distance */}
          <div className={style.routeModalInfo}>
            <div className={style.routeAirportNameContainer}>
              {airport1Details && airport1Details.name && (
                <>
                  <h4 className={style.routeAirportName}>
                    {airport1Details.name}
                  </h4>
                  <p className={style.routeCountry}>
                    {airport1Details.country.name}
                  </p>
                </>
              )}
            </div>

            <div className={style.routeDistanceWrapper}>
              <p className={style.routeDistance}>
                {Math.ceil(route.distance)} km
              </p>
              <p className={style.routeType}>
                {airport1Details.country.name === "Custom Airport" ||
                airport2Details.country.name === "Custom Airport"
                  ? "Custom Route"
                  : airport1Details.country.name ===
                    airport2Details.country.name
                  ? "Domestic"
                  : "International"}
              </p>
            </div>
            <div className={style.routeAirportNameContainer}>
              {airport2Details && airport2Details.name && (
                <>
                  <h4 className={style.routeAirportName}>
                    {airport2Details.name}
                  </h4>
                  <p className={style.routeCountry}>
                    {airport2Details.country.name}
                  </p>
                </>
              )}
            </div>

            {/* Flying time */}
            <div className={style.flightTime}>
              <img src={planeIcon} alt="" />
              <p>{formatTime(travelTime)}</p>
            </div>
          </div>
          {/* Route name */}
          <div>
            <p className={style.routeName}>{route.name}</p>
          </div>
          {/* All travel information */}
          <div className={style.modalDetails}>
            {/* Route type indicators (Scheduled/PSO) */}

            <div className={style.routeCommercialPSO}>
              <div className={style.chipContainer}>
                {route.commercial && (
                  <p
                    className={style.chip}
                    style={chipStyles.scheduled}
                    title="Scheduled route"
                  >
                    Scheduled
                  </p>
                )}
                {route.pso && (
                  <p
                    className={style.chip}
                    style={chipStyles.pso}
                    title="Public Service Obligation"
                  >
                    PSO
                  </p>
                )}
              </div>
              {route.pax > 0 && (
                <div className={style.routePax}>
                  <p>PAX per year</p>
                  <p>{route.pax.toLocaleString()}</p>
                </div>
              )}
            </div>
            <div>
              {" "}
              {/*
      compute all of our per-pax and yearly‐saved numbers once
    */}
              {(() => {
                const atr = route.ATR42_CO2?.CO2_perPassenger ?? 0;
                const es30 = route.ES30_CO2?.CO2_perPassenger ?? 0;
                const noemi = route.ELFLY_NOEMI_CO2?.CO2_perPassenger ?? 0;

                const savedEs30Per = Math.max(0, atr - es30);
                const savedNoemiPer = Math.max(0, atr - noemi);

                // pick which to show in the header:
                let headerSavedPer;
                if (selectedProfile === "heartES30")
                  headerSavedPer = savedEs30Per;
                else if (selectedProfile === "elflyNoemi")
                  headerSavedPer = savedNoemiPer;
                /* default */ else
                  headerSavedPer = Math.max(savedEs30Per, savedNoemiPer);

                const pax = route.pax || 0;
                const savedEs30Year = savedEs30Per * pax;
                const savedNoemiYear = savedNoemiPer * pax;

                const showES30 =
                  selectedProfile === "default" ||
                  selectedProfile === "heartES30";
                const showNoemi =
                  selectedProfile === "default" ||
                  selectedProfile === "elflyNoemi";

                // helper to format annual saved in t if ≥1000 kg, else kg
                const formatAnnual = (kg) =>
                  kg >= 1000
                    ? `${Math.round(kg / 1000)} t`
                    : `${Math.round(kg).toLocaleString()} kg`;

                return (
                  <>
                    {/* header row */}
                    <div
                      className={style.routeCollapseContainer}
                      onClick={toggleEmissionsOpen}
                      role="button"
                      tabIndex={0}
                    >
                      <div className={style.routeCollapseHeader}>
                        {/* pick label & value based on whether pax data exists */}
                        <div className={style.emissionHeading}>
                          {route.pax > 0
                            ? "CO₂ saved per year"
                            : "CO₂ saved per passenger"}
                        </div>
                        <div className={style.routeCollapseHeaderValue}>
                          {route.pax > 0
                            ? // annualSavedKg = savedPer * pax
                              `${Math.round(
                                (headerSavedPer * route.pax) / 1000
                              ).toLocaleString()} t`
                            : `${Math.round(
                                headerSavedPer
                              ).toLocaleString()} kg`}
                          <ExpandMoreIcon
                            className={`${style.icon} ${
                              isEmissionsOpen ? style.routeIconRotate : ""
                            }`}
                          />
                        </div>
                      </div>
                    </div>

                    {/* expanded details */}
                    <Collapse in={isEmissionsOpen} timeout={300} unmountOnExit>
                      <div className={style.routeCollapseContentCO2}>
                        {showES30 && (
                          <p>
                            ES-30 saves {Math.round(savedEs30Per)} kg/pax
                            {pax > 0 && <> ({formatAnnual(savedEs30Year)})</>}
                          </p>
                        )}
                        {showNoemi && (
                          <p>
                            Noemi saves {Math.round(savedNoemiPer)} kg/pax
                            {pax > 0 && <> ({formatAnnual(savedNoemiYear)})</>}
                          </p>
                        )}
                      </div>
                    </Collapse>
                  </>
                );
              })()}
            </div>
            {/* End-to-end travel time information */}
            {filters.compareRoutes && (
              <>
                <div>
                  <div className={style.travelTimesTitle}>
                    <p>City to city travel times</p>
                  </div>
                  <div
                    className={style.routeCollapseContainer}
                    onClick={toggleTimeOpen}
                    role="button"
                    tabIndex={0}
                    // onKeyDown={(e) => {
                    //   if (e.key === "Enter" || e.key === " ") {
                    //     toggleTimeOpen();
                    //   }
                    // }}
                  >
                    <div className={style.routeCollapseHeader}>
                      <div>{getProfileName() || "Aircraft"}</div>
                      <div className={style.routeCollapseHeaderValue}>
                        {formattedFlightTime}
                        <ExpandMoreIcon
                          className={`${style.icon} ${
                            isTimeOpen ? style.routeIconRotate : ""
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                  <Collapse in={isTimeOpen} timeout={400} unmountOnExit>
                    <div className={style.routeCollapseContent}>
                      <ul>
                        <li className={style.routeFlightTimeExpandedValue}>
                          <span>Flight speed</span>
                          <span>{filters.speed} km/h</span>
                        </li>
                        {airport1Details?.municipality ===
                        airport2Details?.municipality ? (
                          // Special case: same municipality
                          <li className={style.routeFlightTimeExpandedValue}>
                            <span>Drive to airport</span>{" "}
                            <span>
                              {formatTime(
                                Math.min(
                                  airport1Details.timeToCityCenter || 0,
                                  airport2Details.timeToCityCenter || 0
                                )
                              )}
                            </span>
                          </li>
                        ) : (
                          // Original case: keep exactly as is
                          airport1Details.timeToCityCenter > 0 && (
                            <li className={style.routeFlightTimeExpandedValue}>
                              <span>Drive to airport</span>{" "}
                              <span>
                                {formatTime(airport1Details.timeToCityCenter)}
                              </span>
                            </li>
                          )
                        )}
                        <li className={style.routeFlightTimeExpandedValue}>
                          <span>Airport Time (Departure)</span>{" "}
                          <span>{formatTime(filters.departure)}</span>
                        </li>
                        <li className={style.routeFlightTimeExpandedValue}>
                          <span>Flight</span>{" "}
                          <span>{formatTime(travelTime)}</span>
                        </li>
                        <li className={style.routeFlightTimeExpandedValue}>
                          <span>Airport Time (Arrival)</span>{" "}
                          <span>{formatTime(filters.arrival)}</span>
                        </li>
                        {airport1Details?.municipality !==
                          airport2Details?.municipality &&
                          airport2Details.timeToCityCenter > 0 && (
                            <li className={style.routeFlightTimeExpandedValue}>
                              <span>Drive from airport</span>{" "}
                              <span>
                                {formatTime(airport2Details.timeToCityCenter)}
                              </span>
                            </li>
                          )}
                      </ul>
                    </div>
                  </Collapse>
                </div>
                {/* Additional travel information (car, public transport, etc.) */}
                {carTimeExists && (
                  <div className={style.routeTextValueContainer}>
                    <div className={style.routeTextStandard}>Car</div>
                    <div className={style.routeTextStandard}>
                      {formatTime(route.travel.car, false, carIsSlower)}
                    </div>
                  </div>
                )}
                {transitTimeExists && (
                  <div className={style.routeTextValueContainer}>
                    <div className={style.routeTextStandard}>
                      Public Transport
                    </div>
                    <div className={style.routeTextStandard}>
                      {formatTime(route.travel.transit, false, transitIsSlower)}
                    </div>
                  </div>
                )}
                {(carTimeExists || transitTimeExists) &&
                  timeDifference != null && (
                    <>
                      <div className={style.routeTextValueContainer}>
                        {/*<div className={style.routeTextStandard}>Ratio</div>
                  <div className={style.routeTextStandard}>
                    {formatRouteSpeedComparison(ratioTime)}
                  </div> */}
                      </div>
                      <div className={style.routeTextValueContainer}>
                        <div className={style.routeTextStandard}>
                          Time Difference
                        </div>
                        <div className={style.routeTextStandard}>
                          {formatTime(timeDifference, true)}
                        </div>
                      </div>
                    </>
                  )}
              </>
            )}
            {/*PriceData component */}
            <>
              <PriceData
                route={route}
                airport1Details={airport1Details}
                airport2Details={airport2Details}
                onExpandChange={setIsPriceExpanded}
              />
            </>
          </div>
        </div>
      </div>
    </div>
  );
};
